import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { useChartTheme, withChartTheme } from "../nerdlets/home/charts/theme";
import { widgetKind } from "./dashboard-api";

/**
 * Renderers for New Relic dashboard widgets, driven by the dashboard's own
 * definition. Only the visualisations this dashboard family uses are
 * implemented; anything else degrades to a readable table rather than an
 * empty box.
 */

/** Columns that carry a time rather than a measurement. */
const TIME_KEY = /^(timestamp|begintimeseconds|endtimeseconds)$|time|date/i;

/** Epoch seconds or milliseconds, in a range no plausible count occupies. */
const looksEpoch = (n) =>
  typeof n === "number" &&
  ((n > 1e11 && n < 4e12) || (n > 1e8 && n < 4e9));

/**
 * A NRQL row is `{facetKey?: ..., measure: number}`; find the measure.
 *
 * Time columns are skipped even though they are numbers. A TIMESERIES row
 * carries both `beginTimeSeconds` and `endTimeSeconds`, so taking "the first
 * number that is not the x axis" picked up the *other* timestamp and plotted
 * epoch seconds as the value — a chart of 1.78 billion where a percentage
 * belonged. Falls back to any number if a row has nothing else.
 */
function primaryMeasure(row, facets) {
  const skip = new Set(facets || []);
  const numeric = Object.keys(row).filter(
    (k) => !skip.has(k) && typeof row[k] === "number"
  );
  const key = numeric.find((k) => !TIME_KEY.test(k)) || numeric[0];
  return key ? { key, value: row[key] } : null;
}

const formatTime = (n) =>
  new Date(n > 4e9 ? n : n * 1000).toLocaleString(undefined, {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatNumber = (n) =>
  typeof n === "number"
    ? Number.isInteger(n)
      ? n.toLocaleString()
      : n.toFixed(2)
    : String(n ?? "");

/**
 * Both the column name and the magnitude have to agree before a number is
 * shown as a date, so a large count in a column merely called "updateTime"
 * is not silently reformatted into 2026.
 */
const formatValue = (key, value) => {
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  if (typeof value !== "number") return String(value ?? "");
  return TIME_KEY.test(key) && looksEpoch(value)
    ? formatTime(value)
    : formatNumber(value);
};

function Billboard({ rows }) {
  const row = rows[0] || {};
  const measure = primaryMeasure(row, []);
  const label = measure?.key;
  return (
    <div className="nrw-billboard">
      <span className="nrw-billboard__value">
        {measure ? formatNumber(measure.value) : "—"}
      </span>
      {label ? <span className="nrw-billboard__label">{label}</span> : null}
    </div>
  );
}

function Table({ rows }) {
  const columns = useMemo(() => {
    const seen = [];
    rows.forEach((r) =>
      Object.keys(r).forEach((k) => !seen.includes(k) && seen.push(k))
    );
    return seen;
  }, [rows]);

  if (!rows.length) return <Empty />;

  return (
    <div className="nrw-table">
      <table>
        <thead>
          <tr>{columns.map((c) => <th key={c} scope="col">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c}>{formatValue(c, row[c])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pie({ rows, theme }) {
  const option = useMemo(() => {
    // A faceted NRQL row has the facet as a string column and the measure as
    // the numeric one.
    const data = rows
      .map((row) => {
        const nameKey = Object.keys(row).find((k) => typeof row[k] === "string");
        const measure = primaryMeasure(row, nameKey ? [nameKey] : []);
        if (!measure) return null;
        return { name: nameKey ? row[nameKey] : measure.key, value: measure.value };
      })
      .filter(Boolean);

    return withChartTheme(
      {
        tooltip: { trigger: "item" },
        legend: { bottom: 0, type: "scroll" },
        series: [
          {
            type: "pie",
            radius: ["45%", "70%"],
            data,
            label: { show: false },
          },
        ],
      },
      theme
    );
  }, [rows, theme]);

  if (!rows.length) return <Empty />;
  return <ReactECharts option={option} style={{ height: "100%", width: "100%" }} />;
}

function Series({ rows, theme, type }) {
  const option = useMemo(() => {
    const xKey =
      Object.keys(rows[0] || {}).find((k) => /time|begin|timestamp/i.test(k)) ||
      Object.keys(rows[0] || {}).find((k) => typeof rows[0][k] === "string");
    const measure = primaryMeasure(rows[0] || {}, xKey ? [xKey] : []);

    return withChartTheme(
      {
        tooltip: { trigger: "axis" },
        grid: { left: 40, right: 16, top: 16, bottom: 28 },
        xAxis: {
          type: "category",
          data: rows.map((r) =>
            looksEpoch(r[xKey]) ? formatTime(r[xKey]) : r[xKey]
          ),
        },
        yAxis: { type: "value" },
        series: [
          {
            type: type === "bar" ? "bar" : "line",
            areaStyle: type === "area" ? {} : undefined,
            smooth: true,
            data: rows.map((r) => (measure ? r[measure.key] : null)),
          },
        ],
      },
      theme
    );
  }, [rows, theme, type]);

  if (!rows.length) return <Empty />;
  return <ReactECharts option={option} style={{ height: "100%", width: "100%" }} />;
}

const Empty = () => <p className="nrw-empty">No chart data available</p>;

/**
 * Sizes a widget by what it actually contains rather than by New Relic's
 * uniform grid slot. A single number does not need the same footprint as a
 * ten-column table, and giving them one was what made the original view hard
 * to scan.
 *
 * Widths are in 12ths; heights in grid rows (see --nrw-row in the CSS).
 */
export function spanFor(kind, rows) {
  if (kind === "billboard") {
    // Four across a row: the number and its label, nothing more.
    return { w: 3, h: 3 };
  }

  if (kind === "pie" || kind === "line" || kind === "area" || kind === "bar") {
    // Charts need vertical room for the plot plus a legend.
    return { w: 4, h: 5 };
  }

  // Tables: width follows column count, height follows row count.
  const columns = countColumns(rows);
  const w = columns >= 5 ? 12 : columns >= 3 ? 6 : 4;

  const n = rows?.length || 0;
  const h = n <= 3 ? 4 : n <= 6 ? 5 : n <= 12 ? 6 : 8;

  return { w, h };
}

function countColumns(rows) {
  const seen = new Set();
  for (const row of rows || []) {
    Object.keys(row).forEach((k) => seen.add(k));
  }
  return seen.size;
}


/**
 * Presentational only — the query lives in useWidgetData, because the grid
 * moves widgets between containers as results arrive and a self-fetching
 * widget would refetch on every move.
 */
export default function Widget({ widget, result, span }) {
  const theme = useChartTheme();
  const kind = widgetKind(widget);
  const rows = result?.rows || [];

  const body = result?.error ? (
    <p className="nrw-error">{result.error.message}</p>
  ) : kind === "billboard" ? (
    <Billboard rows={rows} />
  ) : kind === "pie" ? (
    <Pie rows={rows} theme={theme} />
  ) : kind === "line" || kind === "area" || kind === "bar" ? (
    <Series rows={rows} theme={theme} type={kind} />
  ) : (
    // table, and a sane fallback for any visualisation not implemented here
    <Table rows={rows} />
  );

  // The row packer supplies the share; fall back to the intrinsic one.
  const share = span?.w ?? spanFor(kind, rows).w;

  return (
    <section
      className={`nrw nrw--${kind}`}
      // flex-grow by share, flex-basis 0: every row divides the full width
      // between its widgets, so no row ends short of the right edge.
      style={{ flex: `${share} 1 0%`, minWidth: 0 }}
    >
      <header className="nrw__head">
        <h4 title={widget.title}>{widget.title}</h4>
      </header>
      <div className="nrw__body">{body}</div>
    </section>
  );
}
